import { useState, useEffect } from 'react';
import { CareerFocus, CareerFocusWithMetrics } from './useCareerFocus';
import { getInternshipDuration, getCurrentWeek, calculatePhaseBoundaries } from '../utils/internshipDuration';
import { InternshipPhase } from '../components/InternshipDashboard/InternshipDashboardV2';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'useInternshipPhase' });

/**
 * Centralized hook for determining the current internship phase
 * Uses the boundary-based calculation from internshipDuration.ts
 * @param focus - The career focus object
 * @returns Current phase and phase calculation utilities
 */
export const useInternshipPhase = (focus: CareerFocus | CareerFocusWithMetrics | null | undefined) => {
  const [currentPhase, setCurrentPhase] = useState<InternshipPhase>('onboarding');
  const [phaseInfo, setPhaseInfo] = useState<{
    currentWeek: number;
    totalWeeks: number;
    boundaries: ReturnType<typeof calculatePhaseBoundaries>;
  } | null>(null);

  useEffect(() => {
    if (!focus) {
      setCurrentPhase('onboarding');
      setPhaseInfo(null);
      return;
    }

    const week = getCurrentWeek(focus);
    const totalWeeks = getInternshipDuration(focus);
    const boundaries = calculatePhaseBoundaries(totalWeeks);
    
    // Determine which phase we're in based on dynamic boundaries
    let phase: InternshipPhase = 'transition'; // Default to transition
    
    if (week >= boundaries.onboarding.start && week <= boundaries.onboarding.end) {
      phase = 'onboarding';
    } else if (week >= boundaries.foundation.start && week <= boundaries.foundation.end) {
      phase = 'foundation';
    } else if (week >= boundaries.growth.start && week <= boundaries.growth.end) {
      phase = 'growth';
    } else if (week >= boundaries.impact.start && week <= boundaries.impact.end) {
      phase = 'impact';
    }
    
    setCurrentPhase(phase);
    setPhaseInfo({ currentWeek: week, totalWeeks, boundaries });
    
    logger.debug('Phase calculation', { 
      week, 
      totalWeeks, 
      phase, 
      boundaries 
    });
  }, [focus]);

  // Helper function to get phase by week number (useful for external calculations)
  const getPhaseByWeek = (week: number): InternshipPhase => {
    if (!phaseInfo) return 'onboarding';
    
    const { boundaries } = phaseInfo;
    
    if (week >= boundaries.onboarding.start && week <= boundaries.onboarding.end) {
      return 'onboarding';
    } else if (week >= boundaries.foundation.start && week <= boundaries.foundation.end) {
      return 'foundation';
    } else if (week >= boundaries.growth.start && week <= boundaries.growth.end) {
      return 'growth';
    } else if (week >= boundaries.impact.start && week <= boundaries.impact.end) {
      return 'impact';
    }
    
    return 'transition';
  };

  // Helper to get phase progress (0-1) within current phase
  const getPhaseProgress = (): number => {
    if (!phaseInfo) return 0;
    
    const { currentWeek, boundaries } = phaseInfo;
    const phaseBounds = boundaries[currentPhase];
    
    if (!phaseBounds) return 0;
    
    const phaseLength = phaseBounds.end - phaseBounds.start + 1;
    const weekInPhase = currentWeek - phaseBounds.start + 1;
    
    return Math.min(1, Math.max(0, weekInPhase / phaseLength));
  };

  return {
    currentPhase,
    currentWeek: phaseInfo?.currentWeek || 1,
    totalWeeks: phaseInfo?.totalWeeks || 12,
    boundaries: phaseInfo?.boundaries || calculatePhaseBoundaries(12),
    getPhaseByWeek,
    getPhaseProgress,
    isLoading: !phaseInfo && !!focus
  };
};